import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { GenerateQuestions } from '../assignment/generatequestions';
import QuestionDialog from './questiondialog';
import { generatePrompt } from './generateprompt';
import axios from 'axios';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

export default function PromptDialog({ open, handleClose, prompt, formData }) {
    const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
    const [isQuestionDialogOpen, setQuestionDialogOpen] = useState(false);
    const [generatingQuestions, setGeneratingQuestions] = useState(false);
    const [savingScript, setSavingScript] = useState(false);
    const [addingVideo, setAddingVideo] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [isVideoDialogOpen, setVideoDialogOpen] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [alertMessage, setAlertMessage] = useState('');
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);

    const getBasicAuthHeader = () => {
        const username = process.env.REACT_APP_API_USERNAME;
        const password = process.env.REACT_APP_API_PASSWORD;
        const encodedCredentials = btoa(`${username}:${password}`);
        return `Basic ${encodedCredentials}`;
    };

   // Function to fetch existing videoUrl
   const fetchVideoUrl = async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
        const response = await axios.get(`${apiUrl}/api/${formData.trainingId}/video-url`, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': '*/*',
                'Access-Control-Allow-Origin': '*',
                'Authorization': getBasicAuthHeader(),
            }
        });
        if (response.data) {
            setVideoUrl(response.data);
        }
    } catch (error) {
        //console.error('Error fetching video URL:', error);
    }
};

// Function to check if a script exists
const checkIfScriptExists = async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
        const response = await axios.get(`${apiUrl}/api/script/${formData.trainingId}`, {
            headers: {
                'Content-Type': 'application/json',
                'Accept': '*/*',
                'Access-Control-Allow-Origin': '*',
                'Authorization': getBasicAuthHeader(),
            }
        });
        if (response.status === 200) {
            setIsSaveDisabled(true); // Disable save button if script is found
        } else {
            setIsSaveDisabled(false); // Enable save button if no script is found
        }
    } catch (error) {
        if (error.response && error.response.status === 404) {
            setIsSaveDisabled(false); // Enable save button if script is not found
        } else {
            //console.error('Error checking script existence:', error);
        }
    }
};

// Fetch video URL and check if script exists when dialog opens
useEffect(() => {
    if (isVideoDialogOpen) {
        fetchVideoUrl();;
    }
    
}, [isVideoDialogOpen]);
useEffect(() => {
    if (open) {
        checkIfScriptExists();
    }
}, [open]);

    // Function to handle saving the generated script
    const handleSaveScript = async () => {
        if (!guardAction('action.training.question.saveScript')) return;
        const apiUrl = process.env.REACT_APP_API_URL;
        setSavingScript(true); // Start loading for save button
        try {
            const response = await axios.post(apiUrl + '/api/script', {
                trainingId: formData.trainingId,
                script: prompt,
            }, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': '*/*',
                    'Access-Control-Allow-Origin': '*',
                    'Authorization': getBasicAuthHeader(),
                }
            });

            //console.log('Script saved successfully:', response.data);
            setAlertMessage('Script saved successfully!');
        } catch (error) {
            //console.error('Error saving script:', error);
            setAlertMessage('Failed to save the script. Please try again.');
        } finally {
            setSavingScript(false); // End loading for save button
        }
    };

    // Function to handle generating questions
    async function handleGenerateQuestions() {
        if (!guardAction('action.training.question.generate')) return;
        setGeneratingQuestions(true); // Start loading for generate questions button

        const totalQuestions = formData.questionsPerVideo;
        const questionsList = [];
        let remainingQuestions = totalQuestions;

        while (remainingQuestions > 0) {
            const questionsInBatch = Math.min(15, remainingQuestions); // Adjust batch size as needed
            try {
                const prompt1 = await generatePrompt(formData, prompt);
                //console.log(prompt1);

                const questionsResponse = await GenerateQuestions(prompt1, formData.trainingId);
                let rawQuestionsString = questionsResponse.data.questionResponse.replace(/```json\n|\n```/g, '');

                const jsonStartIndex = rawQuestionsString.indexOf('[');
                if (jsonStartIndex !== -1) {
                    rawQuestionsString = rawQuestionsString.slice(jsonStartIndex);
                }

                const questions = JSON.parse(rawQuestionsString);
                questions.forEach(question => questionsList.push(question));
                remainingQuestions -= questionsInBatch;
            } catch (error) {
                //console.error('Error generating questions:', error);
                setAlertMessage('Failed to generate questions. Please try again.');
                break;
            }
        }

        if (questionsList.length > 0) {
            setQuestions(questionsList);
            //console.log(questionsList)
            setQuestionDialogOpen(true);
        } else {
            setAlertMessage('No questions generated, please try again.');
        }

        setGeneratingQuestions(false); // End loading for generate questions button
    }

    // Function to handle adding a video
    const handleAddVideo = async () => {
        const apiUrl = process.env.REACT_APP_API_URL;
        const trimmedUrl = videoUrl.trim();
    
        // Basic validation
        if (!trimmedUrl) {
            setAlertMessage('Video URL cannot be empty.');
            return;
        }
    
        const body = { videoUrl: trimmedUrl };
        setAddingVideo(true); // Start loading for add video button
    
        try {
            const response = await axios.post(
                `${apiUrl}/api/${formData.trainingId}/video-url`,
                body,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': '*/*',
                        'Access-Control-Allow-Origin': '*',
                        'Authorization': getBasicAuthHeader(),
                    },
                }
            );
    
            if (response.status === 200 || response.status === 201) { // Ensure success check
                setAlertMessage('Video added successfully!');
                setVideoDialogOpen(false); // Close the video dialog on success
            } else {
                setAlertMessage('Unexpected response from the server. Please try again.');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to add video. Please try again.';
            setAlertMessage(errorMessage);
        } finally {
            setAddingVideo(false); // End loading for add video button
            setVideoUrl(''); // Clear input field
        }
    };
    
    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <QuestionDialog open={isQuestionDialogOpen && !generatingQuestions} handleClose={() => setQuestionDialogOpen(false)} questions={questions} trainingId={formData.trainingId} />
                <DialogTitle>Generated Script</DialogTitle>
                <DialogContent>
                    <pre>{prompt}</pre>
                    <IconButton color="primary" onClick={() => setVideoDialogOpen(true)} style={{ marginTop: 16 }}>
                        <AddCircleOutlineIcon /> Add Video
                    </IconButton>
                    {/* Video Dialog */}
                    <Dialog open={isVideoDialogOpen} onClose={() => setVideoDialogOpen(false)} maxWidth="xs" fullWidth>
                        <DialogTitle>Add Video URL</DialogTitle>
                        <DialogContent>
                            <TextField
                                label="Video URL"
                                fullWidth
                                value={videoUrl}
                                onChange={(e) => setVideoUrl(e.target.value)}
                                margin="dense"
                            />
                        </DialogContent>
                        <DialogActions>
                        <Button
        onClick={async () => {
            await handleAddVideo(); // Explicitly await the async function
        }}
        variant="contained"
        color="primary"
        disabled={addingVideo}
    >
        {addingVideo ? <ButtonSpinnerSlot size={24} /> : 'Submit'}
    </Button>
                            <Button onClick={() => setVideoDialogOpen(false)} color="secondary">
                                Cancel
                            </Button>
                        </DialogActions>
                    </Dialog>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleGenerateQuestions} variant="contained" color="primary" disabled={generatingQuestions || savingScript}>
                        {generatingQuestions ? <ButtonSpinnerSlot size={24} /> : 'Generate Questions'}
                    </Button>
                    <Button onClick={handleSaveScript} variant="contained" color="secondary" disabled={savingScript || generatingQuestions || isSaveDisabled}>
                        {savingScript ? <ButtonSpinnerSlot size={24} /> : 'Save'}
                    </Button>
                    <Button onClick={handleClose} sx={{ color: 'black', backgroundColor: 'lightgray' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
        </>
    );
}
